// src/input/rumble.ts — Gamepad titreşimi (haptics). Sim olaylarının görsel/işitsel karşılığının dokunsal eşi.
//
// Neden ayrı dosya: sim üç.js'siz ve yan etkisiz kalmalı; titreşim tamamen sunum katmanı (DESIGN §11 "his cihazları"
// listesinin dokunsal karşılığı). game.ts presenter'ı olayları burada tek çağrıya çevirir.
//
// Tarayıcı gerçeği: `GamepadHapticActuator.playEffect('dual-rumble', …)` Chrome/Edge'de çalışır, Safari'de yoktur;
// Firefox yalnız eski `vibrationActuator.pulse()`u destekler. Hiçbiri yoksa sessizce hiçbir şey yapmayız — try/catch
// ile sarılı, çünkü desteklenmeyen tipte playEffect reddedilen bir Promise döndürür (yakalanmazsa konsolu kirletir).
//
// Titreşim ÜST ÜSTE BİNMEZ: playEffect her çağrıda öncekini keser. Bu yüzden küçük bir öncelik kuralı var — süren güçlü
// bir efekt (knockdown/ring-out) sırasında gelen hafif isabet titreşimi yutulur; aksi hâlde nakavt darbesi jab'la kesilirdi.

import { resolvePadIndex } from './gamepad.ts';

/** Bir titreşim ön ayarı: süre (ms) + güçlü/zayıf motor şiddeti (0–1). */
export interface RumblePreset { duration: number; strong: number; weak: number }

/**
 * Ön ayarlar DESIGN §11 hit stop tablosuyla aynı ailelerden: hafif isabet kısa ve zayıf, ağır daha tok,
 * knockdown/bitirici en güçlü, ring-out uzun ve alçak (düşüş hissi), blok kuru bir tık, parry keskin çift vuruş
 * (playEffect tek darbe verdiği için parry kısa + güçlü zayıf motorla "çınlama" gibi ayarlandı).
 */
// `satisfies` + `as const`: anahtarlar birebir bilinsin (noUncheckedIndexedAccess altında `Record<string, …>` indekslemesi
// `RumblePreset | undefined` verirdi ve her kullanımda `!` gerekirdi).
export const RUMBLE = {
  hitLight: { duration: 60, strong: 0.16, weak: 0.32 },
  hitHeavy: { duration: 110, strong: 0.42, weak: 0.45 },
  knockdown: { duration: 180, strong: 0.75, weak: 0.55 },
  block: { duration: 45, strong: 0.10, weak: 0.22 },
  parry: { duration: 90, strong: 0.30, weak: 0.85 },
  grab: { duration: 70, strong: 0.25, weak: 0.30 },
  ringOut: { duration: 300, strong: 0.85, weak: 0.30 },
  hurt: { duration: 90, strong: 0.30, weak: 0.40 },
} as const satisfies Record<string, RumblePreset>;

/** playEffect'i olan aktüatör (tip tanımı tarayıcılar arasında tutarsız; yapısal tip kullanıyoruz). */
interface Actuator {
  playEffect?: (type: string, opts: { duration: number; strongMagnitude: number; weakMagnitude: number; startDelay?: number }) => Promise<unknown>;
  pulse?: (value: number, duration: number) => Promise<unknown>;
}

/**
 * Oyuncunun pad'ine titreşim gönderir. `enabled = false` ile tamamen susar (ayar menüsüne bağlanabilir).
 * Pad indeksi maçtaki oyuncuyla aynıdır (ileride yerel çok oyuncu: oyuncu başına bir Rumble).
 */
export class Rumble {
  enabled = true;
  padIndex: number;
  /** Süren efektin biteceği zaman (ms, performance.now tabanlı) ve şiddeti — öncelik kuralı için. */
  private busyUntil = 0;
  private busyStrength = 0;
  /** Desteklenmiyorsa bir kez öğrenip bir daha denemeyiz (her isabette reddedilen Promise üretmeyelim). */
  private unsupported = false;

  constructor(padIndex = 0) { this.padIndex = padIndex; }

  /** Ön ayarla titreşim. `now` verilmezse performance.now() okunur. */
  play(preset: RumblePreset, now = typeof performance !== 'undefined' ? performance.now() : 0): void {
    if (!this.enabled || this.unsupported) return;
    // Süren daha güçlü bir efekti kesme (nakavt titreşimini jab bölmesin).
    if (now < this.busyUntil && preset.strong <= this.busyStrength) return;

    const act = this.actuator();
    if (!act) return;
    this.busyUntil = now + preset.duration;
    this.busyStrength = preset.strong;
    try {
      if (typeof act.playEffect === 'function') {
        void act.playEffect('dual-rumble', {
          duration: preset.duration,
          strongMagnitude: preset.strong,
          weakMagnitude: preset.weak,
        }).catch(() => { /* desteklenmeyen tip / izin yok — sessiz */ });
      } else if (typeof act.pulse === 'function') {
        void act.pulse(Math.max(preset.strong, preset.weak), preset.duration).catch(() => {});
      } else {
        this.unsupported = true;
      }
    } catch {
      this.unsupported = true;
    }
  }

  /** Titreşimi hemen kes (duraklatma, maç sonu, menüye dönüş). */
  stop(): void {
    const act = this.actuator();
    this.busyUntil = 0; this.busyStrength = 0;
    if (!act) return;
    try {
      if (typeof act.playEffect === 'function') {
        void act.playEffect('dual-rumble', { duration: 0, strongMagnitude: 0, weakMagnitude: 0 }).catch(() => {});
      }
    } catch { /* yok say */ }
  }

  private actuator(): Actuator | null {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;
    // Yuva sabit değil (bkz. gamepad.ts resolvePadIndex): titreşim, girdinin okunduğu pad'e gitmeli.
    const idx = resolvePadIndex(this.padIndex);
    const pad = idx >= 0 ? navigator.getGamepads()[idx] : null;
    if (!pad) return null;
    // `vibrationActuator` standart; bazı sürümlerde `hapticActuators[0]`.
    const p = pad as unknown as { vibrationActuator?: Actuator; hapticActuators?: Actuator[] };
    return p.vibrationActuator ?? p.hapticActuators?.[0] ?? null;
  }
}
